import { useEffect } from "react";
import CedarRapidsHeroSection from "./components/HeroSection";
import CedarRapidsStickyNav from "./components/StickyNav";
import CedarRapidsProcessingTimeSection from "./components/ProcessingTimeSection";
import CedarRapidsServicesSection from "./components/ServicesSection";
import CedarRapidsLocalAreaSection from "./components/LocalAreaSection";
import CedarRapidsTestimonialsSection from "./components/TestimonialsSection";
import CedarRapidsAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import CedarRapidsComparisonSection from "./components/ComparisonSection";
import CedarRapidsMistakesSection from "./components/MistakesSection";
import CedarRapidsHowItWorksSection from "./components/HowItWorksSection";
import CedarRapidsFAQSection, { faqs } from "./components/FAQSection";
import CedarRapidsHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import CedarRapidsFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function CedarRapidsPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Cedar Rapids IA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/cedar-rapids/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Cedar Rapids, IA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Linn County and eastern Iowa travelers.");
    setMeta("geo.placename", "Cedar Rapids, Iowa");
    setMeta("geo.region", "US-IA");
    setMeta("geo.position", "41.9779;-91.6656");
    setMeta("ICBM", "41.9779, -91.6656");
    setMeta("keywords", "expedited passport, emergency passport, Cedar Rapids passport, same day passport Cedar Rapids, fast passport Iowa, Linn County passport");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Iowa", item: "https://fastpassportcenter.com/iowa/" },
          { "@type": "ListItem", position: 3, name: "Cedar Rapids Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Cedar Rapids — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Cedar Rapids and eastern Iowa travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Cedar Rapids",
          containedInPlace: {
            "@type": "State",
            name: "Iowa",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Cedar Rapids",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <CedarRapidsHeroSection />
      <CedarRapidsStickyNav />
      <CedarRapidsProcessingTimeSection />
      <CedarRapidsServicesSection />
      <CedarRapidsLocalAreaSection />
      <CedarRapidsTestimonialsSection />
      <CedarRapidsAcceptanceFacilitiesSection />
      <CedarRapidsComparisonSection />
      <CedarRapidsMistakesSection />
      <CedarRapidsHowItWorksSection />
      <CedarRapidsFAQSection />
      <CedarRapidsHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/cedar-rapids" state="IA" />
      <CedarRapidsFinalCTA />
    </div>
  );
}